import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, Plus, ExternalLink, Copy, Check, CheckSquare, RotateCcw } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useAgentRefresh, QUERY_KEYS } from '../ai/hooks';
import { AGENT_FOCUS_EVENT } from '../ai/context/AIAgentContext';
import type { UIFocus } from '../ai/types/agent';
import { CreateTodoModal } from '../components/todos';
import { Toast } from '../components/ui';
import { createTodo } from '../services/todoService';
import type { CreateTodoInput } from '../types/todo';
import {
  getSitePageByType,
  updateSitePage,
  createBlock,
  updateBlock,
  deleteBlock,
  updateBlockOrders,
  ensureUserPages,
  generateSlug,
  resetPageToDefault,
} from '../services/siteService';
import { getForms } from '../services/formService';
import { BlockItem } from '../components/site/BlockItem';
import { AddBlockModal } from '../components/site/AddBlockModal';
import { BlockEditorModal } from '../components/site/BlockEditorModal';
import type { SitePageWithBlocks, SiteBlock, BlockType, BlockSettings, SitePageType } from '../types/site';
import { PAGE_TYPE_LABELS } from '../types/site';
import type { Form } from '../types/forms';
import { logger } from '../lib/logger';

const PROTECTED_BLOCKS: BlockType[] = ['HERO', 'FORM'];

function getDefaultSettings(type: BlockType): BlockSettings {
  switch (type) {
    case 'TEXT':
      return { title: '', text: '' };
    case 'IMAGE':
      return { imageUrl: '', caption: '' };
    case 'IMAGE_GALLERY':
      return { images: [] };
    case 'IMAGE_TEXT':
      return { title: '', text: '', imageUrl: '', layout: 'image-right' };
    case 'BEFORE_AFTER':
      return { beforeImage: '', afterImage: '', beforeLabel: 'Vorher', afterLabel: 'Nachher' };
    case 'VIDEO':
      return { videoUrl: '', title: '' };
    case 'BENEFITS':
    case 'HIGHLIGHTS':
      return { title: '', items: [] };
    case 'FAQ':
      return { title: '', items: [] };
    case 'STEPS':
      return { title: '', steps: [] };
    case 'TESTIMONIALS':
      return { title: '', testimonials: [] };
    case 'SOCIAL_PROOF':
      return { title: '', items: [] };
    case 'CTA':
      return { title: '', buttonLabel: '' };
    case 'WHATSAPP_BUTTON':
      return { phoneNumber: '', message: '', buttonLabel: 'WhatsApp Nachricht senden' };
    case 'CUSTOM_HTML':
      return { html: '' };
    default:
      return { title: '' };
  }
}

export function SitePageEditor() {
  const { type } = useParams<{ type: string }>();
  const navigate = useNavigate();
  const { user, profile } = useAuth();
  const pageType = (type?.toUpperCase() || 'BUSINESS') as SitePageType;

  const [page, setPage] = useState<SitePageWithBlocks | null>(null);
  const [forms, setForms] = useState<Form[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [showAddBlock, setShowAddBlock] = useState(false);
  const [editingBlock, setEditingBlock] = useState<SiteBlock | null>(null);
  const [copiedLink, setCopiedLink] = useState(false);
  const [isTodoModalOpen, setIsTodoModalOpen] = useState(false);
  const [showTodoToast, setShowTodoToast] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [focusedBlockId, setFocusedBlockId] = useState<string | null>(null);

  // Refs for block elements to enable scrolling
  const blockRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [slug, setSlug] = useState('');
  const [isPublished, setIsPublished] = useState(false);
  const [primaryColor, setPrimaryColor] = useState('#9B6BFF');
  const [accentColor, setAccentColor] = useState('#E8C6B8');

  const loadPage = useCallback(async () => {
    if (!user) return;
    try {
      const data = await getSitePageByType(user.id, pageType);
      if (data) {
        setPage(data);
        setTitle(data.title);
        setDescription(data.description);
        setSlug(data.slug);
        setIsPublished(data.is_published);
        setPrimaryColor(data.theme_primary_color);
        setAccentColor(data.theme_accent_color);
      }
    } catch (error) {
      logger.error('Error loading page:', error);
    } finally {
      setIsLoading(false);
    }
  }, [user, pageType]);

  useEffect(() => {
    const initializeAndLoad = async () => {
      if (!user || !profile) return;
      try {
        const userSlug = generateSlug(profile.full_name || user.email || 'user');
        await ensureUserPages(user.id, userSlug);
        await loadPage();
        const formsData = await getForms(user.id);
        setForms(formsData);
      } catch (error) {
        logger.error('Error initializing:', error);
      }
    };

    if (user && profile) {
      initializeAndLoad();
    }
  }, [user, profile, pageType, loadPage]);

  // Listen for AI Agent site page updates
  useAgentRefresh([QUERY_KEYS.SITE_PAGES, QUERY_KEYS.SITE_PAGE(pageType), QUERY_KEYS.SITE_BLOCKS(pageType)], loadPage);

  // Listen for AI Agent focus events to highlight/scroll to blocks
  useEffect(() => {
    const handleFocus = (event: Event) => {
      const customEvent = event as CustomEvent<UIFocus>;
      const focus = customEvent.detail;

      if (focus.type === 'block') {
        setFocusedBlockId(focus.id);

        // Scroll to the block after a short delay
        setTimeout(() => {
          const blockElement = blockRefs.current.get(focus.id);
          if (blockElement) {
            blockElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 150);

        // Clear focus after 3 seconds
        setTimeout(() => {
          setFocusedBlockId(null);
        }, 3000);
      }
    };

    window.addEventListener(AGENT_FOCUS_EVENT, handleFocus);
    return () => {
      window.removeEventListener(AGENT_FOCUS_EVENT, handleFocus);
    };
  }, []);

  const handleSave = async () => {
    if (!user || !page) return;
    setIsSaving(true);

    try {
      await updateSitePage(page.id, user.id, {
        title: title.trim(),
        description: description.trim(),
        slug: slug.trim(),
        is_published: isPublished,
        theme_primary_color: primaryColor,
        theme_accent_color: accentColor,
      });
      await loadPage();
    } catch (error) {
      logger.error('Error saving page:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddBlock = async (blockType: BlockType) => {
    if (!user || !page) return;

    try {
      const newBlock = await createBlock(user.id, {
        site_page_id: page.id,
        type: blockType,
        block_order: page.blocks.length,
        visible: true,
        settings: getDefaultSettings(blockType),
      });
      setPage({ ...page, blocks: [...page.blocks, newBlock] });
      setEditingBlock(newBlock);
    } catch (error) {
      logger.error('Error adding block:', error);
    }
  };

  const handleToggleVisibility = useCallback(async (blockId: string, visible: boolean) => {
    if (!user || !page) return;

    try {
      await updateBlock(blockId, user.id, { visible });
      setPage((prev) => prev
        ? { ...prev, blocks: prev.blocks.map((b) => (b.id === blockId ? { ...b, visible } : b)) }
        : prev
      );
    } catch (error) {
      logger.error('Error toggling visibility:', error);
    }
  }, [user, page]);

  const handleUpdateBlockSettings = async (blockId: string, settings: BlockSettings, showInMenu: boolean, menuLabel: string | null) => {
    if (!user || !page) return;

    try {
      await updateBlock(blockId, user.id, { settings, show_in_menu: showInMenu, menu_label: menuLabel });
      setPage({
        ...page,
        blocks: page.blocks.map((b) => (b.id === blockId ? { ...b, settings, show_in_menu: showInMenu, menu_label: menuLabel } : b)),
      });
    } catch (error) {
      logger.error('Error updating block:', error);
    }
  };

  const handleDeleteBlock = useCallback(async (blockId: string) => {
    if (!user || !page) return;
    if (!confirm('Möchtest du diesen Block wirklich löschen?')) return;

    try {
      await deleteBlock(blockId, user.id);
      setPage((prev) => prev
        ? { ...prev, blocks: prev.blocks.filter((b) => b.id !== blockId) }
        : prev
      );
    } catch (error) {
      logger.error('Error deleting block:', error);
    }
  }, [user, page]);

  const handleMoveBlock = useCallback(async (blockId: string, direction: 'up' | 'down') => {
    if (!user || !page) return;

    const currentIndex = page.blocks.findIndex((b) => b.id === blockId);
    if (currentIndex === -1) return;

    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (newIndex < 0 || newIndex >= page.blocks.length) return;

    const newBlocks = [...page.blocks];
    [newBlocks[currentIndex], newBlocks[newIndex]] = [newBlocks[newIndex], newBlocks[currentIndex]];

    const updates = newBlocks.map((b, i) => ({ id: b.id, block_order: i }));

    setPage((prev) => prev ? { ...prev, blocks: newBlocks } : prev);

    try {
      await updateBlockOrders(user.id, updates);
    } catch (error) {
      logger.error('Error reordering blocks:', error);
    }
  }, [user, page]);

  const handleMoveUp = useCallback((id: string) => handleMoveBlock(id, 'up'), [handleMoveBlock]);
  const handleMoveDown = useCallback((id: string) => handleMoveBlock(id, 'down'), [handleMoveBlock]);

  const getPublicUrl = () => {
    const urlPath = pageType === 'BUSINESS' ? 'business' : 'products';
    return `/u/${slug}/${urlPath}`;
  };

  const copyPublicUrl = async () => {
    const url = `${window.location.origin}${getPublicUrl()}`;
    await navigator.clipboard.writeText(url);
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 2000);
  };

  const handleCreateTodo = async (input: CreateTodoInput) => {
    if (!user) return;
    await createTodo(user.id, input);
    setIsTodoModalOpen(false);
    setShowTodoToast(true);
  };

  const handleResetToDefault = async () => {
    if (!user || !page) return;
    if (!confirm('Möchtest du diese Seite wirklich auf das Standard-Layout zurücksetzen? Alle aktuellen Blöcke und Einstellungen werden überschrieben.')) return;

    setIsResetting(true);
    try {
      await resetPageToDefault(page.id, user.id, pageType);
      await loadPage();
    } catch (error) {
      logger.error('Error resetting page:', error);
      alert('Fehler beim Zurücksetzen der Seite');
    } finally {
      setIsResetting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!page) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500 dark:text-slate-400">Seite nicht gefunden</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {showTodoToast && (
        <Toast
          message="Todo erfolgreich erstellt!"
          linkText="Zu den Todos"
          linkTo="/todos"
          onClose={() => setShowTodoToast(false)}
        />
      )}

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/site/pages')}
            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-xl transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-[#1A1A1A] dark:text-white">
              {PAGE_TYPE_LABELS[pageType]}
            </h1>
            {isPublished && (
              <div className="flex items-center gap-2 mt-1">
                <span className="text-xs sm:text-sm text-gray-500 dark:text-slate-400 truncate max-w-[150px] sm:max-w-none">{getPublicUrl()}</span>
                <button
                  onClick={copyPublicUrl}
                  className="p-1 text-gray-400 hover:text-primary transition-colors flex-shrink-0"
                  title="Link kopieren"
                >
                  {copiedLink ? (
                    <Check className="w-4 h-4 text-emerald-500" />
                  ) : (
                    <Copy className="w-4 h-4" />
                  )}
                </button>
                <a
                  href={getPublicUrl()}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-1 text-gray-400 hover:text-primary transition-colors flex-shrink-0"
                  title="Seite öffnen"
                >
                  <ExternalLink className="w-4 h-4" />
                </a>
              </div>
            )}
          </div>
        </div>
        <button
          onClick={handleSave}
          disabled={isSaving}
          className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-primary text-white rounded-xl hover:bg-primary-dark transition-colors font-medium disabled:opacity-50 w-full sm:w-auto"
        >
          <Save className="w-5 h-5" />
          {isSaving ? 'Speichern...' : 'Speichern'}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1 space-y-6 min-w-0">
          <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-100 dark:border-slate-700 p-4 sm:p-6 space-y-4">
            <h2 className="font-semibold text-gray-900 dark:text-white">Seiteneinstellungen</h2>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1.5">Titel (SEO)</label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary bg-white dark:bg-slate-700 text-[#1A1A1A] dark:text-white"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1.5">Beschreibung (SEO)</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={2}
                className="w-full px-4 py-2.5 border border-gray-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary resize-none bg-white dark:bg-slate-700 text-[#1A1A1A] dark:text-white"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1.5">URL-Slug</label>
              <input
                type="text"
                value={slug}
                onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                className="w-full px-4 py-2.5 border border-gray-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary bg-white dark:bg-slate-700 text-[#1A1A1A] dark:text-white"
              />
            </div>

            <div className="flex items-center justify-between py-2">
              <label className="text-sm font-medium text-gray-700 dark:text-slate-300">Veröffentlicht</label>
              <button
                type="button"
                onClick={() => setIsPublished(!isPublished)}
                className={`relative w-12 h-6 rounded-full transition-colors ${
                  isPublished ? 'bg-primary' : 'bg-gray-200 dark:bg-slate-600'
                }`}
              >
                <span
                  className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${
                    isPublished ? 'translate-x-6' : ''
                  }`}
                />
              </button>
            </div>
          </div>

          <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-100 dark:border-slate-700 p-4 sm:p-6 space-y-4">
            <h2 className="font-semibold text-gray-900 dark:text-white">Farbschema</h2>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1.5">Hauptfarbe</label>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={primaryColor}
                  onChange={(e) => setPrimaryColor(e.target.value)}
                  className="w-10 h-10 rounded-lg border border-gray-200 dark:border-slate-600 cursor-pointer"
                />
                <input
                  type="text"
                  value={primaryColor}
                  onChange={(e) => setPrimaryColor(e.target.value)}
                  className="flex-1 px-4 py-2.5 border border-gray-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary bg-white dark:bg-slate-700 text-[#1A1A1A] dark:text-white"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1.5">Akzentfarbe</label>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={accentColor}
                  onChange={(e) => setAccentColor(e.target.value)}
                  className="w-10 h-10 rounded-lg border border-gray-200 dark:border-slate-600 cursor-pointer"
                />
                <input
                  type="text"
                  value={accentColor}
                  onChange={(e) => setAccentColor(e.target.value)}
                  className="flex-1 px-4 py-2.5 border border-gray-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary bg-white dark:bg-slate-700 text-[#1A1A1A] dark:text-white"
                />
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-100 dark:border-slate-700 p-4 sm:p-6">
            <h2 className="font-semibold text-gray-900 dark:text-white mb-3">SEO Vorschau</h2>
            <div className="bg-gray-50 dark:bg-slate-700 rounded-xl p-4">
              <p className="text-blue-600 dark:text-blue-400 text-sm font-medium truncate">{title || 'Seitentitel'}</p>
              <p className="text-green-700 dark:text-green-400 text-xs truncate">{window.location.origin}{getPublicUrl()}</p>
              <p className="text-gray-600 dark:text-slate-400 text-xs mt-1 line-clamp-2">{description || 'Seitenbeschreibung...'}</p>
            </div>
          </div>

          {/* Action Buttons */}
          {user && page && (
            <div className="space-y-3">
              <button
                onClick={() => setIsTodoModalOpen(true)}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gray-100 dark:bg-slate-700 hover:bg-gray-200 dark:hover:bg-slate-600 text-gray-700 dark:text-slate-300 rounded-xl transition-colors font-medium"
              >
                Todo erstellen
                <CheckSquare className="w-5 h-5" />
              </button>

              <button
                onClick={handleResetToDefault}
                disabled={isResetting}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 border border-gray-200 dark:border-slate-600 hover:border-gray-300 dark:hover:border-slate-500 hover:bg-gray-50 dark:hover:bg-slate-700 text-gray-600 dark:text-slate-400 rounded-xl transition-colors font-medium disabled:opacity-50"
              >
                <RotateCcw className={`w-4 h-4 ${isResetting ? 'animate-spin' : ''}`} />
                {isResetting ? 'Wird zurückgesetzt...' : 'Auf Standard zurücksetzen'}
              </button>
            </div>
          )}
        </div>

        <div className="lg:col-span-2 space-y-4 min-w-0">
          <div className="flex items-center justify-between gap-3">
            <h2 className="font-semibold text-gray-900 dark:text-white">Blöcke</h2>
            <button
              onClick={() => setShowAddBlock(true)}
              className="inline-flex items-center gap-2 px-3 sm:px-4 py-2 bg-primary/10 text-primary rounded-xl hover:bg-primary/20 transition-colors font-medium text-sm flex-shrink-0"
            >
              <Plus className="w-4 h-4" />
              <span className="hidden sm:inline">Block hinzufügen</span>
              <span className="sm:hidden">Hinzufügen</span>
            </button>
          </div>

          <div className="space-y-3">
            {page.blocks.map((block, index) => (
              <BlockItem
                key={block.id}
                ref={(el) => {
                  if (el) {
                    blockRefs.current.set(block.id, el);
                  } else {
                    blockRefs.current.delete(block.id);
                  }
                }}
                block={block}
                isFirst={index === 0}
                isLast={index === page.blocks.length - 1}
                isProtected={PROTECTED_BLOCKS.includes(block.type)}
                isFocused={focusedBlockId === block.id}
                onToggleVisibility={handleToggleVisibility}
                onEdit={setEditingBlock}
                onDelete={handleDeleteBlock}
                onMoveUp={handleMoveUp}
                onMoveDown={handleMoveDown}
              />
            ))}
          </div>
        </div>
      </div>

      <AddBlockModal
        isOpen={showAddBlock}
        onClose={() => setShowAddBlock(false)}
        onAdd={handleAddBlock}
      />

      <BlockEditorModal
        block={editingBlock}
        forms={forms}
        pageContext={{ page_type: pageType, page_title: title }}
        profile={profile}
        onClose={() => setEditingBlock(null)}
        onSave={handleUpdateBlockSettings}
      />

      {/* Todo Modal */}
      {user && page && (
        <CreateTodoModal
          isOpen={isTodoModalOpen}
          onClose={() => setIsTodoModalOpen(false)}
          onSubmit={handleCreateTodo}
          defaultTitle={`${PAGE_TYPE_LABELS[pageType]}: ${title}`}
          defaultDescription={pageType === 'BUSINESS' ? 'Seite auf Social Media teilen' : 'Produktbilder aktualisieren'}
          tag="website"
          sourceType="website"
          sourceId={page.id}
        />
      )}
    </div>
  );
}
